/**
 * Client-side image resizer for newsletter images.
 * Downscales oversized images to a max width (default 1200px — 2x of the 600px
 * email column for retina), preserving aspect ratio, and re-encodes as JPEG at
 * 82% quality. Never upscales.
 *
 * Returns a File so it can be appended to FormData like any upload.
 */
export async function resizeImageForEmail(file, {
    maxWidth = 1200,
    maxHeight = 1600,
    quality = 0.82,
    mime = "image/jpeg",
} = {}) {
    // Bail out for non-images or SVG (SVG doesn't rasterize the same way and stays crisp regardless)
    if (!file || !file.type.startsWith("image/") || file.type === "image/svg+xml") {
        return file;
    }

    const bitmap = await createBitmap(file);
    const srcW = bitmap.width;
    const srcH = bitmap.height;

    // If already within bounds AND the file is smaller than ~500 KB, keep the original
    if (srcW <= maxWidth && srcH <= maxHeight && file.size <= 500 * 1024) {
        cleanup(bitmap);
        return file;
    }

    // Compute new size preserving aspect ratio
    const ratio = Math.min(maxWidth / srcW, maxHeight / srcH, 1);
    const dstW = Math.round(srcW * ratio);
    const dstH = Math.round(srcH * ratio);

    const canvas = document.createElement("canvas");
    canvas.width = dstW;
    canvas.height = dstH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, dstW, dstH);
    cleanup(bitmap);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
    if (!blob) return file;

    const baseName = (file.name || "image").replace(/\.[a-z0-9]+$/i, "");
    return new File([blob], `${baseName}.jpg`, { type: mime, lastModified: Date.now() });
}

async function createBitmap(file) {
    if (typeof createImageBitmap === "function") {
        try {
            return await createImageBitmap(file);
        } catch { /* fall through to Image element */ }
    }
    // Fallback for browsers without createImageBitmap
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((resolve, reject) => {
            const im = new Image();
            im.onload = () => resolve(im);
            im.onerror = reject;
            im.src = url;
        });
        return img;
    } finally {
        // don't revoke here — image is used sync after
    }
}

function cleanup(source) {
    if (source && typeof source.close === "function") source.close();
}
